const CROSS_SITE_IDENTITY_COOKIE = 'qp_id'

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

/**
 * True when running over HTTPS. `SameSite=None` cookies (required for the
 * embedded iframe to read the identity cookie cross-site) are rejected by
 * browsers unless they are also `Secure`, which in turn requires HTTPS. Local
 * dev over http://localhost falls back to a `SameSite=Lax` cookie that only
 * works same-site — which is fine because cross-site identity is a
 * backend-backed production concern.
 *
 * @returns {boolean}
 */
const isSecureContext = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  return window.location.protocol === 'https:'
}

/**
 * Reads the cross-site anonymous-identity token from the first-party cookie.
 * Returns null when absent. When called from an embedded iframe the caller
 * must first have been granted storage access (see
 * `requestCrossSiteStorageAccess`), otherwise the partitioned cookie jar is
 * read and the token will appear missing.
 *
 * @returns {string | null}
 */
const readCrossSiteIdentityToken = (): string | null => {
  if (typeof document === 'undefined') {
    return null
  }

  const entries = document.cookie.split(';')

  for (const entry of entries) {
    const separatorIndex = entry.indexOf('=')

    if (separatorIndex === -1) {
      continue
    }

    const name = entry.slice(0, separatorIndex).trim()

    if (name === CROSS_SITE_IDENTITY_COOKIE) {
      return decodeURIComponent(entry.slice(separatorIndex + 1).trim())
    }
  }

  return null
}

/**
 * Writes the cross-site anonymous-identity token to a first-party cookie on
 * the QuickPass app origin. Must be called from a first-party top-level
 * context (the popup), never from the embedded iframe. Uses
 * `SameSite=None; Secure` in production so the iframe can later read it via
 * the Storage Access API.
 *
 * @param {string} token - the anonymous-session token to persist
 * @returns {void}
 */
const writeCrossSiteIdentityToken = (token: string): void => {
  if (typeof document === 'undefined') {
    return
  }

  const encodedToken = encodeURIComponent(token)
  const sameSiteClause = isSecureContext()
    ? 'SameSite=None; Secure'
    : 'SameSite=Lax'

  document.cookie = `${CROSS_SITE_IDENTITY_COOKIE}=${encodedToken}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; ${sameSiteClause}`
}

/**
 * Gesture-free check for whether this document already holds storage access
 * to its unpartitioned cookies. Safe to call on mount — unlike
 * `requestCrossSiteStorageAccess` it never prompts, so it never needs a user
 * gesture. Returns true on browsers without the API (same-site contexts),
 * where the cookie is readable directly.
 *
 * @returns {Promise<boolean>}
 */
const hasCrossSiteStorageAccess = async (): Promise<boolean> => {
  if (typeof document === 'undefined') {
    return false
  }

  if (typeof document.hasStorageAccess !== 'function') {
    return true
  }

  try {
    return await document.hasStorageAccess()
  } catch (error) {
    console.warn('[cross-site-identity] storage access check failed —', error)
    return false
  }
}

/**
 * Asks the browser to grant this (potentially embedded) document access to
 * its unpartitioned first-party cookies via the Storage Access API. Resolves
 * true when access is available — either already granted, or granted now —
 * and false when the browser declines or the API is unsupported.
 *
 * Browsers only grant this after the user has interacted with the QuickPass
 * origin as a first party (which the anonymous-identity popup provides), and
 * usually require a user gesture, so call this from within a click handler.
 *
 * @returns {Promise<boolean>}
 */
const requestCrossSiteStorageAccess = async (): Promise<boolean> => {
  if (typeof document === 'undefined') {
    return false
  }

  if (typeof document.hasStorageAccess !== 'function') {
    return true
  }

  try {
    const alreadyHasAccess = await document.hasStorageAccess()

    if (alreadyHasAccess === true) {
      return true
    }

    if (typeof document.requestStorageAccess !== 'function') {
      return false
    }

    await document.requestStorageAccess()
    return true
  } catch (error) {
    console.warn('[cross-site-identity] storage access denied —', error)
    return false
  }
}

export {
  readCrossSiteIdentityToken,
  writeCrossSiteIdentityToken,
  hasCrossSiteStorageAccess,
  requestCrossSiteStorageAccess,
}
